/**
 * Boot-phase reducer for the /code transcript: folds `boot_progress` events
 * into an ordered phase list the status rows render.
 *
 * Adapted from ColeMurray/background-agents (MIT), commit a27cad16eaff97d8e151fb59d7e9325bd0f9d842
 */
import type { CodeBootPhase, CodeBootProgress } from '@/lib/code-sessions/sandbox/types';
import type { CodeSessionEvent } from '@/lib/code-sessions/types';

import { CODE_BOOT_PHASE } from '@/lib/code-sessions/sandbox/types';

/**
 * `warning` settles a phase that went wrong without failing the boot, as a
 * failed setup script does. `skipped` is the server's choice not to run a
 * phase, as setup for a repository without a script; `cancelled` is a phase
 * the boot ended or moved past before it settled.
 */
export type BootPhaseStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped'
  | 'cancelled'
  | 'warning';

export type BootPhaseState = {
  phase: CodeBootPhase;
  label: string;
  status: BootPhaseStatus;
  elapsedMs?: number;
  /** The note on this phase's latest event; a failed row shows it as the reason. */
  detail?: string;
  /** Epoch ms the server reported this phase starting; drives the running row's live timer. */
  startedAt?: number;
  /** The setup script's last output lines. */
  output?: readonly string[];
  /** The setup row runs a script cubic wrote, because the repository has none saved. */
  detected?: boolean;
  /** The original setup used a script cubic wrote; later saved-script runs keep this history. */
  initiallyDetected?: boolean;
};

export const BOOT_PHASE_LABELS: Record<CodeBootPhase, string> = {
  [CODE_BOOT_PHASE.SNAPSHOT]: 'Saving original session',
  [CODE_BOOT_PHASE.CREATE]: 'Creating sandbox',
  [CODE_BOOT_PHASE.CHECKOUT]: 'Checking out repository',
  [CODE_BOOT_PHASE.SETUP]: 'Running setup script',
  [CODE_BOOT_PHASE.HARNESS]: 'Starting agent',
  [CODE_BOOT_PHASE.READY]: 'Ready',
};

const BOOT_PHASE_ORDER: readonly CodeBootPhase[] = [
  CODE_BOOT_PHASE.SNAPSHOT,
  CODE_BOOT_PHASE.CREATE,
  CODE_BOOT_PHASE.CHECKOUT,
  CODE_BOOT_PHASE.SETUP,
  CODE_BOOT_PHASE.HARNESS,
  CODE_BOOT_PHASE.READY,
];

/**
 * The initial phase list shown before any boot_progress event arrives. Only a
 * fork snapshots its parent first, so only a fork lists that phase; a fork
 * inherits its parent's setup, so it does not list setup.
 */
export function seedSandboxBoot({ fork = false }: { fork?: boolean } = {}): BootPhaseState[] {
  return BOOT_PHASE_ORDER.filter((phase) =>
    fork ? phase !== CODE_BOOT_PHASE.SETUP : phase !== CODE_BOOT_PHASE.SNAPSHOT,
  ).map((phase) => ({
    phase,
    label: BOOT_PHASE_LABELS[phase],
    status: 'pending',
  }));
}

/** The ready phase completed: the agent is up. */
function isReady(boot: readonly BootPhaseState[]): boolean {
  return boot.some(
    (entry) => entry.phase === CODE_BOOT_PHASE.READY && entry.status === 'completed',
  );
}

/** Apply one boot_progress event, advancing the phase list in place-free style. */
export function applyBootProgress(
  boot: readonly BootPhaseState[],
  progress: CodeBootProgress,
  /** When the server emitted the event, in epoch ms. */
  at?: number,
): BootPhaseState[] {
  if (progress.status === 'progress') {
    // New output for a running phase: its timer and every other phase stay as they are.
    return boot.map((entry) =>
      entry.phase === progress.phase
        ? {
            ...entry,
            detail: progress.detail ?? entry.detail,
            output: progress.output ?? entry.output,
          }
        : entry,
    );
  }
  const started = progress.status === 'started';
  // Run again starts the setup script beside a ready agent: only the setup row
  // restarts, and the ready report closing the run finds the rest as it was.
  const setupRerun = started && progress.phase === CODE_BOOT_PHASE.SETUP && isReady(boot);
  const status: BootPhaseStatus = progress.status === 'started' ? 'running' : progress.status;
  return boot.map((entry) => {
    if (entry.phase !== progress.phase) {
      // Only a phase starting changes the other rows.
      if (!started || setupRerun) {
        return entry;
      }
      if (BOOT_PHASE_ORDER.indexOf(entry.phase) > BOOT_PHASE_ORDER.indexOf(progress.phase)) {
        return {
          ...entry,
          status: 'pending',
          elapsedMs: undefined,
          detail: undefined,
          startedAt: undefined,
        };
      }
      if (entry.status === 'failed') {
        return { ...entry, status: 'cancelled' };
      }
      // A phase starting implicitly completes every earlier pending phase.
      if (
        BOOT_PHASE_ORDER.indexOf(entry.phase) < BOOT_PHASE_ORDER.indexOf(progress.phase) &&
        entry.status === 'pending'
      ) {
        return { ...entry, status: 'completed' };
      }
      return entry;
    }
    return {
      ...entry,
      status,
      elapsedMs: started ? progress.elapsedMs : (progress.elapsedMs ?? entry.elapsedMs),
      detail: progress.detail,
      startedAt: started ? (at ?? entry.startedAt) : entry.startedAt,
      output: started ? undefined : (progress.output ?? entry.output),
      // A start says it itself: a detected run's row is marked while it runs,
      // and a later run's start clears it.
      detected: started ? progress.detected : (progress.detected ?? entry.detected),
      initiallyDetected: entry.initiallyDetected ?? progress.detected,
    };
  });
}

/**
 * Boot is over (ready or failed); collapse remaining pending phases. A setup
 * script running again after the agent is ready keeps running until it reports.
 */
export function endBootPhase(boot: readonly BootPhaseState[]): BootPhaseState[] {
  const failed = boot.some((entry) => entry.status === 'failed');
  const ready = isReady(boot);
  return boot.map((entry) =>
    (entry.status === 'pending' || entry.status === 'running') &&
    !(ready && entry.phase === CODE_BOOT_PHASE.SETUP && entry.status === 'running')
      ? { ...entry, status: failed ? 'cancelled' : 'completed' }
      : entry,
  );
}

/** True once the ready phase completes or any phase fails. */
export function isBootFinished(boot: readonly BootPhaseState[]): boolean {
  return boot.some((entry) => entry.status === 'failed') || isReady(boot);
}

/**
 * The failure a failed sandbox implies while setup is in progress: the phase
 * running, or the next one when startup stopped between phases. Startup can
 * fail before the phase in progress reports it.
 */
function interruptedPhaseFailure(
  boot: readonly BootPhaseState[],
  at: number,
): CodeBootProgress | undefined {
  if (isBootFinished(boot) || boot.every((entry) => entry.status === 'pending')) {
    return undefined;
  }
  const interrupted =
    boot.find((entry) => entry.status === 'running') ??
    boot.find((entry) => entry.status === 'pending');
  if (!interrupted) {
    return undefined;
  }
  return {
    phase: interrupted.phase,
    status: 'failed',
    elapsedMs:
      interrupted.startedAt === undefined ? undefined : Math.max(0, at - interrupted.startedAt),
  };
}

/** Fold a session's events into its boot rows. */
export function foldBootEvents(
  events: readonly CodeSessionEvent[],
  { fork = false }: { fork?: boolean } = {},
): BootPhaseState[] {
  let boot = seedSandboxBoot({ fork });
  let setupReported = false;
  for (const event of events) {
    if (event.kind === 'boot_progress') {
      boot = applyBootProgress(boot, event.progress, event.at);
      setupReported ||= event.progress.phase === CODE_BOOT_PHASE.SETUP;
    } else if (event.kind === 'sandbox_status' && event.status === 'pending') {
      // A sandbox cubic replaced boots anew.
      boot = seedSandboxBoot({ fork });
      setupReported = false;
    } else if (event.kind === 'sandbox_status' && event.status === 'failed') {
      const failure = interruptedPhaseFailure(boot, event.at);
      if (failure) {
        boot = applyBootProgress(boot, failure, event.at);
      }
    }
  }
  const folded = isBootFinished(boot) ? endBootPhase(boot) : boot;
  // No setup event, yet a later phase's start settled the seeded setup row: a
  // boot from before setup existed, or one older than the loaded events (each
  // turn reports its agent starting again). Nothing shows a script ran, so drop
  // the row unless it is still pending or a startup failure failed it.
  return setupReported
    ? folded
    : folded.filter(
        (entry) =>
          entry.phase !== CODE_BOOT_PHASE.SETUP ||
          entry.status === 'pending' ||
          entry.status === 'failed',
      );
}

/** One sandbox's boot rows, and when cubic began it in place of the session's earlier sandbox. */
export type SandboxBoot = { phases: BootPhaseState[]; at?: number };

/** Fold a session's events into one boot per sandbox it had, the current sandbox's last. */
export function foldSandboxBoots(
  events: readonly CodeSessionEvent[],
  options: { fork?: boolean } = {},
): SandboxBoot[] {
  const boots: { at?: number; events: CodeSessionEvent[] }[] = [{ events: [] }];
  for (const event of events) {
    // A sandbox cubic replaced boots anew; the earlier one's boot keeps its events. A repeated
    // announcement while a boot still runs belongs to that boot.
    const current = boots.at(-1)!;
    if (
      event.kind === 'sandbox_status' &&
      event.status === 'pending' &&
      isBootFinished(foldBootEvents(current.events, options))
    ) {
      boots.push({ at: event.at, events: [] });
    }
    boots.at(-1)!.events.push(event);
  }
  return boots.map((boot) => ({ phases: foldBootEvents(boot.events, options), at: boot.at }));
}
